import React, { useMemo, useState } from "react";
import SectionLabel from "../../components/SectionLabel";
import Ticket from "../../components/Ticket";
import RouteStrip from "../../components/RouteStrip";
import Combobox from "../../components/Combobox";
import { formatDate } from "../../lib/format";
import { BR_AIRLINES, BR_AIRPORTS, BR_TRAVEL_AGENCIES } from "../../lib/brazil-aviation";
import "./NewCase.css";

const AIRLINE_OPTIONS = BR_AIRLINES.map((a) => ({
  value: a.iata,
  name: a.name,
  iata: a.iata,
}));

const AIRPORT_OPTIONS = BR_AIRPORTS.map((a) => ({
  value: a.iata,
  city: a.city,
  uf: a.uf,
  name: a.name,
  iata: a.iata,
}));

const AGENCY_OPTIONS = BR_TRAVEL_AGENCIES.map((a) => ({
  value: a.slug,
  name: a.name,
  slug: a.slug,
}));

const agencyLabel = (o) => o.name;
const agencySearch = (o) => `${o.name} ${o.slug}`;
const renderAgency = (o) => (
  <>
    <span className="trunfo-combobox__option-primary">{o.name}</span>
  </>
);

const airlineLabel = (o) => `${o.name} (${o.iata})`;
const airlineSearch = (o) => `${o.name} ${o.iata}`;
const renderAirline = (o) => (
  <>
    <span className="trunfo-combobox__option-primary">{o.name}</span>
    <span className="trunfo-combobox__option-secondary">{o.iata}</span>
  </>
);

const airportLabel = (o) => `${o.city} (${o.iata})`;
const airportSearch = (o) => `${o.city} ${o.uf} ${o.iata} ${o.name}`;
const renderAirport = (o) => (
  <>
    <span className="trunfo-combobox__option-primary">
      {o.city} · {o.name}
    </span>
    <span className="trunfo-combobox__option-secondary">
      {o.iata} — {o.uf}
    </span>
  </>
);

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.content : "";
}

function initialForm(form) {
  const values = form?.values ?? {};
  return {
    problem_type_slug: values.problem_type_slug ?? "",
    title_override: values.title ?? "",
    happened_at: values.happened_at ?? "",
    incident_data: {
      airline_iata: values.airline_iata ?? "",
      flight_code: values.flight_code ?? "",
      origin_iata: values.origin_iata ?? "",
      destination_iata: values.destination_iata ?? "",
      travel_agency: values.travel_agency ?? "",
      notes: values.notes ?? "",
    },
  };
}

function buildTitle(problem, incident) {
  const problemName = problem?.name ?? "Caso";
  const route =
    incident.origin_iata && incident.destination_iata
      ? `${incident.origin_iata} \u2192 ${incident.destination_iata}`
      : "";
  const flight = incident.flight_code || "";
  const parts = [flight, route].filter(Boolean).join(" ");
  return parts ? `${problemName} \u00b7 ${parts}` : problemName;
}

function initialStep(form) {
  if (form?.errors && Object.keys(form.errors).length > 0) return 2;
  return 0;
}

export default function NewCase({ problem_types = [], form = {} }) {
  const [state, setState] = useState(() => initialForm(form));
  const [step, setStep] = useState(() => initialStep(form));

  const selectedProblem = useMemo(
    () => problem_types.find((p) => p.slug === state.problem_type_slug) ?? null,
    [problem_types, state.problem_type_slug]
  );

  const derivedTitle = useMemo(
    () => buildTitle(selectedProblem, state.incident_data),
    [selectedProblem, state.incident_data]
  );
  const effectiveTitle = state.title_override.trim() || derivedTitle;

  const updateField = (key, value) => setState((s) => ({ ...s, [key]: value }));
  const updateIncident = (key, value) =>
    setState((s) => ({ ...s, incident_data: { ...s.incident_data, [key]: value } }));

  const canAdvanceFromStep1 = !!selectedProblem;
  const canAdvanceFromStep2 = state.happened_at.length > 0;

  return (
    <main className="trunfo-wizard">
      <header className="trunfo-wizard__header">
        <SectionLabel tone="accent">Novo caso · Trunfo Voos</SectionLabel>
        <h1 className="trunfo-wizard__title">
          Vamos montar seu <em>trunfo</em>.
        </h1>
        <p className="trunfo-wizard__lede">
          Três passos rápidos: o que aconteceu, os detalhes do voo, e uma revisão antes de abrir o
          caso.
        </p>
      </header>

      <Stepper step={step} />

      {step === 0 && (
        <StepProblem
          problemTypes={problem_types}
          selectedSlug={state.problem_type_slug}
          onSelect={(slug) => updateField("problem_type_slug", slug)}
          onNext={() => setStep(1)}
          canAdvance={canAdvanceFromStep1}
        />
      )}

      {step === 1 && (
        <StepFlight
          state={state}
          errors={form.errors}
          onField={updateField}
          onIncident={updateIncident}
          onBack={() => setStep(0)}
          onNext={() => setStep(2)}
          canAdvance={canAdvanceFromStep2}
        />
      )}

      {step === 2 && (
        <StepReview
          state={state}
          problem={selectedProblem}
          derivedTitle={derivedTitle}
          effectiveTitle={effectiveTitle}
          onTitleOverride={(value) => updateField("title_override", value)}
          onBack={() => setStep(1)}
        />
      )}
    </main>
  );
}

function Stepper({ step }) {
  const labels = ["O que aconteceu", "Detalhes do voo", "Revisar e abrir"];
  return (
    <ol className="trunfo-wizard__stepper" aria-label="Passos">
      {labels.map((label, i) => (
        <li
          key={label}
          className={
            "trunfo-wizard__step" +
            (i === step ? " trunfo-wizard__step--current" : "") +
            (i < step ? " trunfo-wizard__step--done" : "")
          }
          aria-current={i === step ? "step" : undefined}
        >
          <span className="trunfo-wizard__step-index">{String(i + 1).padStart(2, "0")}</span>
          <span className="trunfo-wizard__step-label">{label}</span>
        </li>
      ))}
    </ol>
  );
}

function StepProblem({ problemTypes, selectedSlug, onSelect, onNext, canAdvance }) {
  return (
    <section className="trunfo-wizard__panel">
      <SectionLabel tone="mute">Passo 01</SectionLabel>
      <h2 className="trunfo-wizard__panel-title">Qual foi o problema?</h2>
      <p className="trunfo-wizard__panel-lede">
        Escolha o cenário que mais se aproxima. Dá pra ajustar depois.
      </p>

      <ul className="trunfo-wizard__cards" role="radiogroup" aria-label="Tipo de problema">
        {problemTypes.map((pt) => {
          const checked = pt.slug === selectedSlug;
          return (
            <li key={pt.slug}>
              <label
                className={"trunfo-wizard__card" + (checked ? " trunfo-wizard__card--checked" : "")}
              >
                <input
                  type="radio"
                  name="problem_type_slug"
                  value={pt.slug}
                  checked={checked}
                  onChange={() => onSelect(pt.slug)}
                />
                <span className="trunfo-wizard__card-name">{pt.name}</span>
                <span className="trunfo-wizard__card-summary">{pt.summary}</span>
              </label>
            </li>
          );
        })}
      </ul>

      <div className="trunfo-wizard__actions">
        <button
          type="button"
          className="trunfo-btn trunfo-btn--primary"
          onClick={onNext}
          disabled={!canAdvance}
        >
          Continuar
        </button>
      </div>
    </section>
  );
}

function StepFlight({ state, errors = {}, onField, onIncident, onBack, onNext, canAdvance }) {
  const fieldError = (key) => errors?.[key]?.[0];
  return (
    <section className="trunfo-wizard__panel">
      <SectionLabel tone="mute">Passo 02</SectionLabel>
      <h2 className="trunfo-wizard__panel-title">Detalhes do voo</h2>
      <p className="trunfo-wizard__panel-lede">
        Quanto mais preciso o relato, mais forte fica o argumento.
      </p>

      {fieldError("title") ? <p className="trunfo-field__error">{fieldError("title")}</p> : null}

      <div className="trunfo-wizard__grid">
        <Field label="Quando aconteceu" error={fieldError("happened_at")}>
          <input
            type="datetime-local"
            lang="pt-BR"
            value={state.happened_at}
            onChange={(e) => onField("happened_at", e.target.value)}
            onClick={(e) => e.currentTarget.showPicker?.()}
          />
        </Field>

        <Field label="Companhia">
          <Combobox
            value={state.incident_data.airline_iata}
            onChange={(v) => onIncident("airline_iata", v)}
            options={AIRLINE_OPTIONS}
            placeholder="Digite a companhia ou IATA"
            getLabel={airlineLabel}
            getSearchText={airlineSearch}
            renderOption={renderAirline}
          />
        </Field>

        <Field label="Número do voo">
          <input
            type="text"
            value={state.incident_data.flight_code}
            onChange={(e) => onIncident("flight_code", e.target.value.toUpperCase().slice(0, 8))}
            placeholder="LA3456"
          />
        </Field>

        <Field label="Origem">
          <Combobox
            value={state.incident_data.origin_iata}
            onChange={(v) => onIncident("origin_iata", v)}
            options={AIRPORT_OPTIONS}
            placeholder="Digite cidade, IATA ou nome"
            getLabel={airportLabel}
            getSearchText={airportSearch}
            renderOption={renderAirport}
          />
        </Field>

        <Field label="Destino">
          <Combobox
            value={state.incident_data.destination_iata}
            onChange={(v) => onIncident("destination_iata", v)}
            options={AIRPORT_OPTIONS}
            placeholder="Digite cidade, IATA ou nome"
            getLabel={airportLabel}
            getSearchText={airportSearch}
            renderOption={renderAirport}
          />
        </Field>

        <Field label="Agência de viagem">
          <Combobox
            value={state.incident_data.travel_agency}
            onChange={(v) => onIncident("travel_agency", v)}
            options={AGENCY_OPTIONS}
            placeholder="Onde comprou a passagem?"
            getLabel={agencyLabel}
            getSearchText={agencySearch}
            renderOption={renderAgency}
            allowCustom
          />
        </Field>

        <Field label="Observações" full error={fieldError("notes")}>
          <textarea
            rows={4}
            value={state.incident_data.notes}
            onChange={(e) => onIncident("notes", e.target.value)}
            placeholder="Conte, com suas palavras, o que foi informado pela companhia."
          />
        </Field>
      </div>

      <div className="trunfo-wizard__actions">
        <button type="button" className="trunfo-btn" onClick={onBack}>
          Voltar
        </button>
        <button
          type="button"
          className="trunfo-btn trunfo-btn--primary"
          onClick={onNext}
          disabled={!canAdvance}
        >
          Revisar
        </button>
      </div>
    </section>
  );
}

function Field({ label, error, full, children }) {
  return (
    <label className={"trunfo-field" + (full ? " trunfo-field--full" : "")}>
      <span className="trunfo-field__label">{label}</span>
      {children}
      {error ? <span className="trunfo-field__error">{error}</span> : null}
    </label>
  );
}

function StepReview({ state, problem, derivedTitle, effectiveTitle, onTitleOverride, onBack }) {
  const incident = state.incident_data;
  const origin = incident.origin_iata || "—";
  const destination = incident.destination_iata || "—";
  const flight = incident.flight_code || incident.airline_iata || "—";
  const happenedLabel = state.happened_at ? formatDate(state.happened_at) : "—";
  const [editingTitle, setEditingTitle] = useState(state.title_override.trim().length > 0);

  return (
    <section className="trunfo-wizard__panel">
      <SectionLabel tone="mute">Passo 03</SectionLabel>
      <h2 className="trunfo-wizard__panel-title">Confere tudo e abre o caso.</h2>

      <div className="trunfo-wizard__title-edit">
        <SectionLabel tone="mute">Título do caso</SectionLabel>
        {editingTitle ? (
          <div className="trunfo-wizard__title-edit-row">
            <input
              type="text"
              className="trunfo-wizard__title-input"
              value={state.title_override}
              onChange={(e) => onTitleOverride(e.target.value)}
              maxLength={160}
              placeholder={derivedTitle}
              autoFocus
            />
            <button
              type="button"
              className="trunfo-wizard__title-link"
              onClick={() => {
                onTitleOverride("");
                setEditingTitle(false);
              }}
            >
              Usar sugestão
            </button>
          </div>
        ) : (
          <div className="trunfo-wizard__title-edit-row">
            <p className="trunfo-wizard__title-current">{effectiveTitle}</p>
            <button
              type="button"
              className="trunfo-wizard__title-link"
              onClick={() => setEditingTitle(true)}
            >
              Editar
            </button>
          </div>
        )}
      </div>

      <Ticket
        header={
          <>
            <SectionLabel>Novo caso</SectionLabel>
            <SectionLabel tone="accent">{problem?.name ?? "—"}</SectionLabel>
          </>
        }
        stub={
          <>
            <SectionLabel tone="mute">Data</SectionLabel>
            <SectionLabel tone="accent">{happenedLabel}</SectionLabel>
          </>
        }
      >
        <RouteStrip
          origin={origin}
          destination={destination}
          flightCode={flight}
          meta={effectiveTitle}
        />
        {incident.notes ? <p className="trunfo-wizard__notes">{incident.notes}</p> : null}
      </Ticket>

      <form method="post" action="/cases" className="trunfo-wizard__form">
        <input type="hidden" name="authenticity_token" value={csrfToken()} />
        <input type="hidden" name="case[problem_type_slug]" value={state.problem_type_slug} />
        <input type="hidden" name="case[title]" value={effectiveTitle} />
        <input type="hidden" name="case[happened_at]" value={state.happened_at} />
        <input type="hidden" name="case[airline_iata]" value={incident.airline_iata} />
        <input type="hidden" name="case[flight_code]" value={incident.flight_code} />
        <input type="hidden" name="case[origin_iata]" value={incident.origin_iata} />
        <input type="hidden" name="case[destination_iata]" value={incident.destination_iata} />
        <input type="hidden" name="case[travel_agency]" value={incident.travel_agency} />
        <input type="hidden" name="case[notes]" value={incident.notes} />

        <div className="trunfo-wizard__actions">
          <button type="button" className="trunfo-btn" onClick={onBack}>
            Voltar
          </button>
          <button type="submit" className="trunfo-btn trunfo-btn--primary">
            <span aria-hidden="true" className="trunfo-btn__plus">
              +
            </span>
            Abrir caso
          </button>
        </div>
      </form>
    </section>
  );
}
